/* * Version: 3.8.0
 * Letzte Änderung: 16.09.2026, 15:10
 * Änderungshistorie: UX-Verbesserung: Der Haupt-Button "Ergebnis speichern" triggert nun automatisch auch das Speichern von geänderten Namen und Wertungen (Dropdowns), falls die kleinen Haken nicht manuell geklickt wurden.
 * Copyright: BAIT-Tech
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Numpad from './Numpad';
import { renamePlayer, reclassifyEntry, reclassifyTeam } from '../api';
import { toast } from 'react-toastify';

const NUMBER_OF_SETS = 4;
const initializeSets = () => Array.from({ length: NUMBER_OF_SETS }, () => ({ volle: '', abr: '', fw: '' }));

const ScoreEntryModal = ({ scheduleEntry, existingScore, tournamentId, onClose, onSave, onPlayerNameUpdate, availableClassifications = [], onEntryUpdate, availableTeamClassifications = [], onTeamUpdate }) => {
  const [sets, setSets] = useState(initializeSets());
  const [activeInput, setActiveInput] = useState(null);
  
  const [editablePlayerName, setEditablePlayerName] = useState('');
  const [isNameDirty, setIsNameDirty] = useState(false);
  
  const [editableClassification, setEditableClassification] = useState('');
  const [isClassificationDirty, setIsClassificationDirty] = useState(false);
  
  const [editableTeamClassification, setEditableTeamClassification] = useState('');
  const [isTeamClassificationDirty, setIsTeamClassificationDirty] = useState(false);
  
  const inputRefs = useRef({});
  const entry = scheduleEntry?.entry;

  const tabOrder = useMemo(() => {
    const order = [];
    for (let i = 0; i < NUMBER_OF_SETS; i++) {
      order.push({ setIndex: i, field: 'volle' });
      order.push({ setIndex: i, field: 'abr' });
      order.push({ setIndex: i, field: 'fw' });
    }
    return order;
  }, []);

  useEffect(() => {
    if (existingScore && existingScore.sets) {
      const formattedSets = Array.from({ length: NUMBER_OF_SETS }, (_, i) => ({
        volle: existingScore.sets[i]?.volle || '',
        abr: existingScore.sets[i]?.abr || '',
        fw: existingScore.sets[i]?.fw || '',
      }));
      setSets(formattedSets);
    } else {
      setSets(initializeSets());
    }
  }, [existingScore]);

  useEffect(() => {
    if (entry?.player) { 
      const fName = entry.player.firstName || '';
      const lName = entry.player.lastName || '';
      setEditablePlayerName(`${fName} ${lName}`.trim() || 'Unbekannter Spieler'); 
      setIsNameDirty(false); 
    }
    if (entry) { 
      setEditableClassification(entry.classification || ''); 
      setIsClassificationDirty(false); 
    }
    if (entry?.team) { 
      setEditableTeamClassification(entry.team.classification || ''); 
      setIsTeamClassificationDirty(false); 
    }
  }, [entry]);

  const handleChange = (setIndex, field, value) => {
    const numValue = value === '' ? '' : parseInt(value, 10);
    if (isNaN(numValue) || numValue > 999) return;
    setSets(prev => {
      const newSets = [...prev];
      newSets[setIndex] = { ...newSets[setIndex], [field]: numValue };
      return newSets;
    });
  };
  
  const handleNumpadKeyPress = (key) => { if (!activeInput) return; const { setIndex, field } = activeInput; const currentValue = (sets[setIndex][field] || '').toString(); handleChange(setIndex, field, currentValue + key); };
  const handleNumpadBackspace = () => { if (!activeInput) return; const { setIndex, field } = activeInput; const currentValue = (sets[setIndex][field] || '').toString(); handleChange(setIndex, field, currentValue.slice(0, -1)); };
  const handleNumpadClear = () => { if (activeInput) handleChange(activeInput.setIndex, activeInput.field, ''); };
  
  const handleTab = (direction) => { 
    if (!activeInput) { 
      const firstInput = tabOrder[0]; 
      setActiveInput(firstInput); 
      inputRefs.current[`set-${firstInput.setIndex}-${firstInput.field}`]?.focus(); 
      return; 
    } 
    const currentIndex = tabOrder.findIndex(item => item.setIndex === activeInput.setIndex && item.field === activeInput.field); 
    let nextIndex = (currentIndex + direction + tabOrder.length) % tabOrder.length; 
    const nextInput = tabOrder[nextIndex]; 
    setActiveInput(nextInput); 
    inputRefs.current[`set-${nextInput.setIndex}-${nextInput.field}`]?.focus(); 
  };

  const handleNameChange = (e) => { 
    setEditablePlayerName(e.target.value); 
    const originalName = `${entry.player.firstName || ''} ${entry.player.lastName || ''}`.trim(); 
    setIsNameDirty(e.target.value.trim() !== originalName); 
  };

  const handleSaveName = async () => { 
    if (!isNameDirty) return; 
    const nameParts = editablePlayerName.trim().split(' ').filter(Boolean); 
    if (nameParts.length < 2) { 
      toast.error('Bitte Vor- und Nachnamen angeben.'); 
      return; 
    } 
    const lastName = nameParts.pop(); 
    const firstName = nameParts.join(' '); 
    try { 
      const updatedPlayer = await renamePlayer(entry.player._id, { firstName, lastName }); 
      onPlayerNameUpdate(scheduleEntry.id, updatedPlayer); 
      setIsNameDirty(false); 
      toast.success("Name gespeichert."); 
    } catch (error) { 
      toast.error('Name konnte nicht gespeichert werden.'); 
      setEditablePlayerName(`${entry.player.firstName || ''} ${entry.player.lastName || ''}`.trim()); 
      setIsNameDirty(false); 
    } 
  };

  const handleClassificationChange = (e) => { 
    setEditableClassification(e.target.value); 
    setIsClassificationDirty(e.target.value !== entry.classification); 
  };

  const handleSaveClassification = async () => { 
    if (!isClassificationDirty) return; 
    try { 
      const updatedEntry = await reclassifyEntry(entry._id, editableClassification); 
      onEntryUpdate(updatedEntry); 
      setIsClassificationDirty(false); 
      toast.success("Wertung gespeichert."); 
    } catch (error) { 
      toast.error('Wertung konnte nicht gespeichert werden.'); 
      setEditableClassification(entry.classification); 
      setIsClassificationDirty(false); 
    } 
  };

  const handleTeamClassificationChange = (e) => { 
    setEditableTeamClassification(e.target.value); 
    setIsTeamClassificationDirty(e.target.value !== entry.team.classification); 
  };

  const handleSaveTeamClassification = async () => { 
    if (!isTeamClassificationDirty) return; 
    try { 
      const updatedTeam = await reclassifyTeam(entry.team._id, editableTeamClassification); 
      onTeamUpdate(updatedTeam); 
      setIsTeamClassificationDirty(false); 
      toast.success("Mannschafts-Wertung gespeichert."); 
    } catch (error) { 
      toast.error('Mannschafts-Wertung konnte nicht gespeichert werden.'); 
      setEditableTeamClassification(entry.team.classification); 
      setIsTeamClassificationDirty(false); 
    } 
  };

  const totals = useMemo(() => {
    const result = sets.reduce((acc, set) => { 
      acc.totalVolle += Number(set.volle) || 0; 
      acc.totalAbr += Number(set.abr) || 0; 
      acc.totalFw += Number(set.fw) || 0; 
      return acc; 
    }, { totalVolle: 0, totalAbr: 0, totalFw: 0 });
    result.totalGesamt = result.totalVolle + result.totalAbr;
    return result;
  }, [sets]);

  // NEU: Die handleSubmit-Funktion ist nun async und kümmert sich um ALLE ausstehenden Änderungen
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 1. Prüfen und Speichern, ob der Name geändert, aber der Haken nicht geklickt wurde
    if (isNameDirty) {
      await handleSaveName();
    }
    
    // 2. Prüfen und Speichern, ob die Einzel-Wertung geändert wurde
    if (isClassificationDirty) {
      await handleSaveClassification();
    }
    
    // 3. Prüfen und Speichern, ob die Mannschafts-Wertung geändert wurde
    if (isTeamClassificationDirty) {
      await handleSaveTeamClassification();
    }

    // 4. Speichern der eigentlichen Ergebnisse (Punkte)
    const setsToSave = sets.map(set => ({ volle: Number(set.volle) || 0, abr: Number(set.abr) || 0, fw: Number(set.fw) || 0 }));
    const gameMode = entry.gameMode || 'CLASSIC_120';
    
    // Löst das Speichern im Parent-Element aus (welches dann das Modal schließt)
    onSave(entry._id, tournamentId, gameMode, 'qualification', setsToSave);
  };

  if (!entry) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal-content score-modal">
        <div className="score-modal-header">
          <h2>Ergebnis für</h2>
          <input type="text" className="player-name-input" value={editablePlayerName} onChange={handleNameChange} onBlur={handleSaveName}/>
          {isNameDirty && (<button type="button" onClick={handleSaveName} className="btn-save-inline" title="Namen speichern">✓</button>)}
        </div>
        
        <div className="score-modal-details">
          <span>Einzel-Wertung:</span>
          <select value={editableClassification} onChange={handleClassificationChange}>
            {availableClassifications.map(cls => (<option key={cls} value={cls}>{cls}</option>))}
          </select>
          {isClassificationDirty && (<button type="button" onClick={handleSaveClassification} className="btn-save-inline" title="Wertung speichern">✓</button>)}
        </div>
        
        {entry.team && (
          <div className="score-modal-details">
            <span>Mannschafts-Wertung ({entry.team.name}):</span>
            <select value={editableTeamClassification} onChange={handleTeamClassificationChange}>
              {availableTeamClassifications.map(cls => (<option key={cls} value={cls}>{cls}</option>))}
            </select>
            {isTeamClassificationDirty && (<button type="button" onClick={handleSaveTeamClassification} className="btn-save-inline" title="Mannschafts-Wertung speichern">✓</button>)}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="score-entry-layout">
            <div className="score-table-container">
              <table>
                <thead>
                  <tr><th>Satz</th><th>Volle</th><th>Abräumen</th><th>FW</th><th>Gesamt</th></tr>
                </thead>
                <tbody>
                  {sets.map((set, index) => (
                    <tr key={index}>
                      <td>Satz {index + 1}</td>
                      {['volle', 'abr', 'fw'].map(field => (
                        <td key={field}>
                          <input 
                            ref={el => inputRefs.current[`set-${index}-${field}`] = el} 
                            type="number" 
                            value={set[field]} 
                            onFocus={() => setActiveInput({ setIndex: index, field })} 
                            onChange={e => handleChange(index, field, e.target.value)} 
                            readOnly={window.matchMedia("(pointer: coarse)").matches}
                          />
                        </td>
                      ))}
                      <td>{(Number(set.volle) || 0) + (Number(set.abr) || 0)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th>Gesamt</th>
                    <th>{totals.totalVolle}</th>
                    <th>{totals.totalAbr}</th>
                    <th>{totals.totalFw}</th>
                    <th>{totals.totalGesamt}</th>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="numpad-wrapper">
              <Numpad onKeyPress={handleNumpadKeyPress} onBackspace={handleNumpadBackspace} onClear={handleNumpadClear} onNext={() => handleTab(1)} onPrev={() => handleTab(-1)}/>
            </div>
          </div>
          <div className="form-buttons">
            <button type="submit">Ergebnis speichern</button>
            <button type="button" className="btn-cancel" onClick={onClose}>Schließen</button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default ScoreEntryModal;